import type { Conversation } from "./types";

type ViewerRole = "parent" | "teacher" | "admin" | "director";

function formatRoleLabel(role: string): string {
  return role.charAt(0).toUpperCase() + role.slice(1);
}

/**
 * Role-aware conversation title.
 *
 * Parent:   "Emma — Sarah Johnson (Teacher)"
 * Employee: "Jane Smith — re: Emma"
 * Admin:    "Emma Smith · Butterfly Room (Jane Smith)"
 */
export function getConversationTitle(
  conv: Conversation,
  viewerRole: ViewerRole,
): string {
  // Non-child conversations (broadcast, parent-direct) — use existing logic
  if (!conv.child) {
    if (conv.parent_user) return conv.parent_user.full_name ?? "Parent";
    if (conv.classroom) {
      return (
        conv.classroom.name +
        (conv.latest_message.is_broadcast ? " (Broadcast)" : "")
      );
    }
    return "Conversation";
  }

  const childName = `${conv.child.first_name} ${conv.child.last_name}`;

  if (viewerRole === "parent") {
    // Parent sees: "Emma — Sarah Johnson (Teacher)"
    if (conv.last_staff_sender) {
      return `${conv.child.first_name} — ${conv.last_staff_sender.full_name} (${formatRoleLabel(conv.last_staff_sender.role)})`;
    }
    return conv.child.first_name;
  }

  if (viewerRole === "teacher") {
    // Employee sees: "Jane Smith — re: Emma"
    if (conv.parent_user) {
      return `${conv.parent_user.full_name} — re: ${conv.child.first_name}`;
    }
    return childName;
  }

  // Admin / Director sees: "Emma Smith · Butterfly Room (Jane Smith)"
  const parts = [childName];
  if (conv.classroom) {
    parts.push(conv.classroom.name);
  }
  const base = parts.join(" · ");
  if (conv.parent_user) {
    return `${base} (${conv.parent_user.full_name})`;
  }
  return base;
}
